import { isStoreCartLineActive } from './storeCartLineSelection.utils';
import type { ICartItemDetail, ICartItemPayload } from '@/common/interfaces/cart.interface';
import type {
  IDesignGroupColorOption,
  IDesignGroupProductOption,
  IDesignItem,
} from '@/common/interfaces/design.interface';
import { getDesignGroupCartPayload } from '@/utilities/CartUtils';

const buildDefaultDesignLicensing = (
  designId: number
): IDesignGroupColorOption['designLicensing'] => ({
  id: 0,
  collegiate_status: 'no_marks',
  greek_status: 'no_marks',
  collegiate_comment: '',
  greek_comment: '',
  design_id: designId,
  collegiate_duplicate_item_id: 0,
  greek_duplicate_item_id: 0,
  has_collegiate_mark: 0,
  has_greek_mark: 0,
  collegiate_comment_by: '',
  greek_comment_by: '',
});

const mapCartItemToColorOption = (item: ICartItemDetail): IDesignGroupColorOption =>
  ({
    designId: item.designId || 0,
    colorName: item.apparelColor || '',
    colorHex: item.apparelHex || '#ccc',
    hexCode: item.apparelHex || '#ccc',
    primaryImage: item.primaryImage || item.frontImage || '',
    secondaryImage: item.backImage || '',
    designImage: item.designImgPrimary || '',
    proofItemId: item.proofItemId || 0,
    apparelColor: item.apparelColor || '',
    colorCode: item.apparelColorCode || '',
    designPricing: item.designPricing,
    designLicensing: item.designLicensing || buildDefaultDesignLicensing(item.designId || 0),
    apparel: {
      styleCode: item.apparelStyleCode || '',
      name: item.apparelName || '',
      brand: item.apparelBrand || '',
      color: item.apparelColor || '',
      hexCode: item.apparelHex || '#ccc',
    },
  } as IDesignGroupColorOption);

export const buildCartOnlyProductOptionsFromItems = (
  cartItems: ICartItemDetail[]
): IDesignGroupProductOption[] => {
  const productOptionsMap = cartItems.reduce((acc, item) => {
    const productKey = `${item.apparelName}-${item.apparelStyleCode}`;

    if (!acc[productKey]) {
      acc[productKey] = {
        styleCode: item.apparelStyleCode || '',
        apparelName: item.apparelName || '',
        primaryImage: item.primaryImage || item.frontImage || '',
        colors: [],
        proofItemId: item.proofItemId || 0,
        designPricing: item.designPricing,
      } as unknown as IDesignGroupProductOption;
    }

    acc[productKey].colors.push(mapCartItemToColorOption(item));

    return acc;
  }, {} as Record<string, IDesignGroupProductOption>);

  return Object.values(productOptionsMap);
};

export const getDesignGroupProductOptionsFromDesignItem = (
  designItem: IDesignItem | undefined
): IDesignGroupProductOption[] => designItem?.designGroupDesigns?.transformedDesigns ?? [];

export const resolveStoreCartDesignItemProofItemId = (
  cartItems: ICartItemDetail[],
  fallbackProofItemId: number
): number => {
  // Color/product options are children; design groups are keyed to the parent proof item.
  const groupRoot = cartItems.find((item) => (item.designGroupItemId ?? 0) > 0);

  if (groupRoot?.designGroupItemId) {
    return groupRoot.designGroupItemId;
  }

  const candidate = cartItems.find((item) => item.proofItemId > 0);

  return candidate?.proofItemId ?? fallbackProofItemId;
};

export const getUniqueStoreCartProofItemIds = (
  cartItems: ICartItemDetail[],
  fallbackProofItemId: number
): number[] => {
  const ids = cartItems.flatMap((item) => {
    const proofItemIds: number[] = [];
    const designGroupItemId = item.designGroupItemId ?? 0;

    if (designGroupItemId > 0) {
      proofItemIds.push(designGroupItemId);
    }

    if (item.proofItemId > 0) {
      proofItemIds.push(item.proofItemId);
    }

    return proofItemIds;
  });

  if (fallbackProofItemId > 0) {
    ids.unshift(fallbackProofItemId);
  }

  return [...new Set(ids)];
};

export const buildStoreCartOptionSelectionKey = (styleCode: string, designId: number): string =>
  `${styleCode.trim().toUpperCase()}:${designId}`;

export const buildInitialColorSelectionKeys = (cartItems: ICartItemDetail[]): Set<string> =>
  new Set(
    cartItems.map((item) => buildStoreCartOptionSelectionKey(item.apparelStyleCode, item.designId))
  );

export const normalizeStoreCartProductStyleCode = (styleCode: string): string =>
  styleCode.trim().toUpperCase();

export const countSelectedColorsForProduct = (
  selectedColorKeys: Set<string>,
  product: IDesignGroupProductOption
): number =>
  product.colors.filter((color) =>
    selectedColorKeys.has(buildStoreCartOptionSelectionKey(product.styleCode, color.designId))
  ).length;

export const getDefaultColorSelectionKeyForProduct = (
  product: IDesignGroupProductOption
): string | null => {
  const firstColor = product.colors[0];

  if (!firstColor) {
    return null;
  }

  return buildStoreCartOptionSelectionKey(product.styleCode, firstColor.designId);
};

export const clearProductColorSelectionKeys = (
  selectedColorKeys: Set<string>,
  product: IDesignGroupProductOption
): Set<string> => {
  const next = new Set(selectedColorKeys);

  product.colors.forEach((color) => {
    next.delete(buildStoreCartOptionSelectionKey(product.styleCode, color.designId));
  });

  return next;
};

export const ensureProductHasDefaultColorSelected = (
  selectedColorKeys: Set<string>,
  product: IDesignGroupProductOption
): Set<string> => {
  if (countSelectedColorsForProduct(selectedColorKeys, product) > 0) {
    return selectedColorKeys;
  }

  const defaultColorKey = getDefaultColorSelectionKeyForProduct(product);

  if (!defaultColorKey) {
    return selectedColorKeys;
  }

  return new Set(selectedColorKeys).add(defaultColorKey);
};

export const findFirstSelectedColorForProduct = (
  product: IDesignGroupProductOption,
  selectedColorKeys: Set<string>
): IDesignGroupColorOption | null =>
  product.colors.find((color) =>
    selectedColorKeys.has(buildStoreCartOptionSelectionKey(product.styleCode, color.designId))
  ) ?? null;

export const resolveNextColorPreviewAfterDeselect = (
  product: IDesignGroupProductOption,
  nextColorKeys: Set<string>,
  deselectedColor: IDesignGroupColorOption
): IDesignGroupColorOption | null => {
  if (!product.colors.length || !nextColorKeys.size) {
    return null;
  }

  const deselectedIndex = product.colors.findIndex(
    (color) => color.designId === deselectedColor.designId
  );
  const startIndex = deselectedIndex >= 0 ? deselectedIndex : 0;

  for (let offset = 1; offset <= product.colors.length; offset += 1) {
    const color = product.colors[(startIndex + offset) % product.colors.length];
    const key = buildStoreCartOptionSelectionKey(product.styleCode, color.designId);

    if (nextColorKeys.has(key)) {
      return color;
    }
  }

  return findFirstSelectedColorForProduct(product, nextColorKeys);
};

export interface IStoreCartOptionPreviewState {
  productStyleCode: string;
  colorOption: IDesignGroupColorOption | null;
}

export const resolveNextProductPreviewAfterDeselect = (
  productOptions: IDesignGroupProductOption[],
  nextProductKeys: Set<string>,
  nextColorKeys: Set<string>,
  deselectedProduct: IDesignGroupProductOption
): IStoreCartOptionPreviewState | null => {
  if (!productOptions.length || !nextProductKeys.size) {
    return null;
  }

  const deselectedNormalizedStyleCode = normalizeStoreCartProductStyleCode(
    deselectedProduct.styleCode
  );
  const deselectedIndex = productOptions.findIndex(
    (product) =>
      normalizeStoreCartProductStyleCode(product.styleCode) === deselectedNormalizedStyleCode
  );
  const startIndex = deselectedIndex >= 0 ? deselectedIndex : 0;

  for (let offset = 1; offset <= productOptions.length; offset += 1) {
    const product = productOptions[(startIndex + offset) % productOptions.length];
    const normalizedStyleCode = normalizeStoreCartProductStyleCode(product.styleCode);

    if (nextProductKeys.has(normalizedStyleCode)) {
      return {
        productStyleCode: product.styleCode,
        colorOption: findFirstSelectedColorForProduct(product, nextColorKeys),
      };
    }
  }

  return null;
};

export const buildInitialProductSelectionKeys = (cartItems: ICartItemDetail[]): Set<string> =>
  new Set(cartItems.map((item) => normalizeStoreCartProductStyleCode(item.apparelStyleCode)));

export const mergeDesignGroupProductOptions = (
  existing: IDesignGroupProductOption[],
  incoming: IDesignGroupProductOption[]
): IDesignGroupProductOption[] => {
  const productMap = new Map<string, IDesignGroupProductOption>();

  [...existing, ...incoming].forEach((product) => {
    const normalizedStyleCode = product.styleCode.trim().toUpperCase();
    const currentProduct = productMap.get(normalizedStyleCode);

    if (!currentProduct) {
      productMap.set(normalizedStyleCode, {
        ...product,
        colors: [...product.colors],
      });
      return;
    }

    const seenDesignIds = new Set(currentProduct.colors.map((color) => color.designId));

    product.colors.forEach((color) => {
      if (!seenDesignIds.has(color.designId)) {
        currentProduct.colors.push(color);
        seenDesignIds.add(color.designId);
      }
    });
  });

  return Array.from(productMap.values());
};

export const getDesignGroupProductOptionsKey = (options: IDesignGroupProductOption[]): string =>
  options
    .map(
      (product) =>
        `${normalizeStoreCartProductStyleCode(product.styleCode)}:${product.colors
          .map((color) => color.designId)
          .join(',')}`
    )
    .join('|');

export const resolveStoreCartDesignGroupProductOptions = (
  designItem: IDesignItem | undefined,
  cartItems: ICartItemDetail[]
): IDesignGroupProductOption[] => {
  const fromCart = buildCartOnlyProductOptionsFromItems(cartItems);
  const fromDesignItem = getDesignGroupProductOptionsFromDesignItem(designItem);

  if (!fromDesignItem.length) {
    return fromCart;
  }

  if (!fromCart.length) {
    return fromDesignItem;
  }

  return mergeDesignGroupProductOptions(fromCart, fromDesignItem);
};

export const isColorOptionInCart = (
  cartItems: ICartItemDetail[],
  designId: number,
  styleCode: string
): boolean =>
  cartItems.some((item) => item.designId === designId && item.apparelStyleCode === styleCode);

export const isColorOptionActiveInCart = (
  cartItems: ICartItemDetail[],
  designId: number,
  styleCode: string
): boolean =>
  cartItems.some(
    (item) =>
      item.designId === designId &&
      item.apparelStyleCode === styleCode &&
      isStoreCartLineActive(item)
  );

export const isProductOptionActiveInCart = (
  cartItems: ICartItemDetail[],
  styleCode: string
): boolean =>
  cartItems.some((item) => item.apparelStyleCode === styleCode && isStoreCartLineActive(item));

export const isProductOptionInCart = (cartItems: ICartItemDetail[], styleCode: string): boolean =>
  cartItems.some((item) => item.apparelStyleCode === styleCode);

export const findCartFormIndexForColorOption = (
  cartDetails: ICartItemDetail[],
  groupFormIndices: number[],
  designId: number,
  styleCode: string
): number | undefined => {
  const matchIndex = groupFormIndices.find((idx) => {
    const item = cartDetails[idx];
    return item?.designId === designId && item?.apparelStyleCode === styleCode;
  });

  return matchIndex;
};

export const getCartItemIdsForProductOption = (
  cartDetails: ICartItemDetail[],
  groupFormIndices: number[],
  styleCode: string
): string[] =>
  groupFormIndices
    .map((idx) => cartDetails[idx])
    .filter((item): item is ICartItemDetail => Boolean(item) && item.apparelStyleCode === styleCode)
    .map((item) => String(item.cartItemId));

export const buildStoreCartAddPayloadForColorOption = (
  designItem: IDesignItem,
  colorOption: IDesignGroupColorOption,
  productOption: IDesignGroupProductOption,
  minQuantity: number
): ICartItemPayload | null => {
  const quantityKey = `${productOption.styleCode}-${colorOption.colorName}-${colorOption.designId}`;
  const payloads = getDesignGroupCartPayload(designItem, true, 0, {
    [quantityKey]: String(minQuantity),
  });

  return (
    payloads.find(
      (payload) =>
        payload.designId === colorOption.designId &&
        payload.apparelStyleCode === productOption.styleCode
    ) ?? null
  );
};

export const resolveStoreCartDesignGroupMinQuantity = (cartItems: ICartItemDetail[]): number => {
  const referenceItem = cartItems[0];

  if (!referenceItem) {
    return 12;
  }

  return referenceItem.minQuant || referenceItem.quantity || 12;
};

const parseStoreCartColorHexToRgb = (hex: string): [number, number, number] | null => {
  const normalized = hex.replace('#', '').trim();

  if (normalized.length === 3) {
    return [
      Number.parseInt(normalized[0] + normalized[0], 16),
      Number.parseInt(normalized[1] + normalized[1], 16),
      Number.parseInt(normalized[2] + normalized[2], 16),
    ];
  }

  if (normalized.length === 6) {
    return [
      Number.parseInt(normalized.slice(0, 2), 16),
      Number.parseInt(normalized.slice(2, 4), 16),
      Number.parseInt(normalized.slice(4, 6), 16),
    ];
  }

  return null;
};

export const isLightStoreCartColorHex = (hex: string | null | undefined): boolean => {
  const rgb = parseStoreCartColorHexToRgb(hex || '');

  if (!rgb) {
    return false;
  }

  const [r, g, b] = rgb;

  return (r * 299 + g * 587 + b * 114) / 1000 >= 225;
};
